import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { DropdownOption } from 'src/app/models/dropdown-option.model';
import {
  AddEJournalRequest,
  ClearSearchedPhysicalActivityList,
  EditRecordsRequest,
  FetchSearchedPhysicalDailyActivityRequest,
} from '../../core/my-journal.actions';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import {
  GetSearchedPhysicalActivityList,
  GetTodType,
} from '../../core/my-journal.selector';
import { CommonService } from 'src/app/modules/shared/services/common.service';
import { AddEJournalDetailsModel } from '../../model/list.model';
import { EJournalConstant } from '../../constant/e-journal.enum';
import * as Validation from '../../../../shared/constants/validation.constants';

@Component({
  selector: 'app-add-daily-physical-activity',
  templateUrl: './add-daily-physical-activity.component.html',
  styleUrls: ['./add-daily-physical-activity.component.css'],
})
export class AddDailyPhysicalActivityComponent implements OnInit, OnDestroy {
  @Input() data: any;
  errorMessages: any = {};
  errorMessageMap = {
    todType: {
      required: 'This field is required.',
    },
    name: {
      required: 'This field is required.',
    },
    intensity: {
      required: 'This field is required.',
    },
    duration: {
      required: 'This field is required.',
      max: 'Maximum 240 minutes allowed.',
      min: 'Maximum 1 minutes allowed.'
    },
    noOfSteps: {
      max: 'Maximum 24,000 steps allowed.',
      min: 'Minimum 100 steps required',
    },
    points: {},
  };
  todLoading: boolean = false;
  timeOfDayType: DropdownOption[] = [];
  intensityType: DropdownOption[] = [
    {
      key: 100,
      value: '100',
      disabled: false,
    },
    {
      key: 200,
      value: '200',
      disabled: false
    },
    {
      key: 300,
      value: '300',
      disabled: false
    },
    {
      key: 400,
      value: '400',
      disabled: false
    },
  ];
  dataSource: any[] = [];
  colDef = ['activity', 'intensity'];
  searchedHasNext: boolean;
  loading: boolean = false;
  searchedPageNumber: number;
  mode: string;
  showPointError = false;
  validation = Validation;
  form: FormGroup = new FormGroup({
    todType: new FormControl('', [Validators.required]),
    name: new FormControl('', [Validators.required]),
    intensity: new FormControl(null),
    duration: new FormControl(null, [
      Validators.max(240),
      Validators.min(1)
    ]),
    noOfSteps: new FormControl(null, [
      Validators.max(24000),
      Validators.min(100)
    ]),
    points: new FormControl('', [Validators.required]),
    comments: new FormControl(''),
  });
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store,
    private commonService: CommonService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(ClearSearchedPhysicalActivityList());
    this.store
      .select(GetSearchedPhysicalActivityList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((searched) => {
        this.dataSource = searched.list;
        this.loading = searched.loading;
        this.searchedPageNumber = searched.pageNumber;
        this.searchedHasNext = searched.hasNext;
      });

    this.store.select(GetTodType).subscribe(todType => {
      this.timeOfDayType = todType?.list.filter(item => item.key !== 4);
      this.todLoading = todType.loading
    })

    this.form.controls['duration'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((value) => {
        if (
          value &&
          (!this.form.controls['intensity'].dirty ||
            !this.form.controls['intensity'].value) &&
          this.intensityType.length
        ) {
          this.form.controls['intensity'].setValue(this.intensityType[0]?.key);
        }
      });

    this.form.controls['intensity'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((value) => {
        const intensity = +value;
        const duration = +this.form.controls['duration'].value;
        const points = intensity * duration;
        if (value) {
          this.form.controls['noOfSteps'].disable({ emitEvent: false });
          this.form.controls['points'].setValue(points);
        } else {
          if (!this.form.controls['duration'].value) {
            this.form.controls['noOfSteps'].enable({ emitEvent: false });
            this.form.controls['points'].setValue('');
          } else {
            this.form.controls['points'].setValue(points);
          }
        }
      });

    this.form.controls['duration'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((value) => {
        const intensity = +this.form.controls['intensity'].value;
        const duration = +value;
        const points = intensity * duration;
        if (value) {
          this.form.controls['noOfSteps'].disable({ emitEvent: false });
          this.form.controls['points'].setValue(points);
        } else {
          if (!this.form.controls['intensity'].value) {
            this.form.controls['noOfSteps'].enable({ emitEvent: false });
            this.form.controls['points'].setValue('');
          } else {
            this.form.controls['points'].setValue(points);
          }
        }
      });

    this.form.controls['noOfSteps'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((value) => {
        if (value) {
          this.form.controls['duration'].disable({ emitEvent: false });
          this.form.controls['intensity'].disable({ emitEvent: false });
          const rawValue = this.form.controls['noOfSteps'].value;
          const numericValue = rawValue != null
            ? parseInt(rawValue.toString().replace(/,/g, ''), 10)
            : null;

          this.form.controls['points'].setValue(numericValue);
        } else {
          this.form.controls['duration'].enable({ emitEvent: false });
          this.form.controls['intensity'].enable({ emitEvent: false });
          this.form.controls['points'].setValue('');
        }
      });

    this.form.controls['name'].valueChanges
      .pipe(takeUntil(this.onDestroy), debounceTime(1000))
      .subscribe((value) => {
        const trimmedValue = value?.trim();
        if (trimmedValue) {
          this.store.dispatch(
            FetchSearchedPhysicalDailyActivityRequest({
              payload: {
                pageNumber: 1,
                searchText: trimmedValue,
              },
            }),
          );
        } else {
          this.store.dispatch(ClearSearchedPhysicalActivityList());
        }
      });

    if (this.data?.previousData) {
      this.mode = 'edit';
      this.form.patchValue({
        todType: this.data?.previousData.type,
        name: this.data?.previousData.activity,
        intensity: this.data?.previousData.intensity,
        duration: this.data?.previousData.duration,
        noOfSteps: this.data?.previousData.noOfSteps,
        points: this.data?.previousData.points,
        comments: this.data?.previousData.comments || '',
      });
    } else {
      this.mode = 'add';
    }

    this.form.controls['points'].disable();
  }

  onAddPhysicalActivity() {
    const { points } = this.form.getRawValue();
    this.showPointError = !+points;
    this.errorMessages = {};
    if (this.form.invalid) {
      this.errorMessages = this.commonService.checkFormValidation(
        this.form,
        this.errorMessageMap,
      );
      return;
    }
    if (this.showPointError) {
      return;
    }

    if (this.mode === 'add') {
      const physicalActivity: any[] = [];
      physicalActivity.push({
        ...this.form.value,
        points: this.form.controls['points'].value,
      });
      const payload: AddEJournalDetailsModel['request'] = {
        date: this.data?.data?.selectedDate?.date,
        ...(this.data?.data?.memberId
          ? { forUserId: +this.data?.data.memberId }
          : {}),
        ...(this.data?.data?.groupId
          ? { groupId: +this.data?.data.groupId }
          : {}),
        foodIntake: [],
        physicalActivity: physicalActivity,
        screenTime: [],
      };

      if (!this.data?.data?.selectedDate?.isNormalJournalingPAExists) {
        this.store.dispatch(AddEJournalRequest({
          payload: payload
        }))
        return;
      }

      this.commonService.openConfirmationDialog({
        data: {
          message: `You have a <span class="text-[rgb(89,179,169)]">Physical Activity</span> total in your Behavior Tracker for this date. Do you want to delete your Behavior Tracker total and replace with the total of your e-Journal entries?`,
          submitText: 'Yes',
          classList: 'w-[40vw]',
          handleSubmit: () => {
            this.commonService.closeDialog();
            this.store.dispatch(
              AddEJournalRequest({
                payload: payload,
              }),
            );
          },
          cancelText: 'No',
          handleCancel: () => {
            this.commonService.closeDialog();
          },
        },
      });
    } else {
      const payload = {
        ...this.form.value,
        points: this.form.controls['points'].value,
      };
      this.store.dispatch(
        EditRecordsRequest({
          payload: {
            data: payload,
            ...(this.data?.data?.memberId
              ? { forUserId: +this.data?.data.memberId }
              : {}),
            date: this.data?.data?.selectedDate?.date,
            params: {
              recordId: this.data.previousData.id,
              recordType: EJournalConstant.physicalActivityRecordType,
            },
          },
        }),
      );
    }
  }

  onCloseModel() {
    this.commonService.closeDialog();
  }

  onScroll() {
    if (this.searchedHasNext && !this.loading) {
      this.store.dispatch(
        FetchSearchedPhysicalDailyActivityRequest({
          payload: {
            pageNumber: this.searchedPageNumber + 1,
            searchText: this.form.controls['name'].value?.trim(),
          },
        }),
      );
    }
  }

  isNextCall() {
    return this.searchedHasNext && !this.loading;
  }

  getIntensityIcon(value: string): string | null {
    // Return SVG icon name for intensity values
    if (value === '100') {
      return '100 (1)-20250401-134935';
    } else if (value === '200') {
      return '200 (1)-20250401-135016';
    } else if (value === '300') {
      return '300-20250401-135033';
    } else if (value === '400') {
      return '400-20250401-135052';
    }
    return null;
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
